import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { compareVersionsNewestFirst } from '../../utils/projectFileStreamVersions';
import { AlertCircle, Loader2, Send, X } from 'lucide-react';
import {
  defaultRfqStagingDisplayLabel,
  releaseProjectFileToRfqStaging,
  type RfqStagingReleasableStreamKind,
} from '../../utils/rfqHubFileReleaseService';
import type { ProjectFile, RfqReleasePurpose } from '../../types';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useCRM } from '../../utils/CRMContext';
import { inferProposalLinkFromAppliedFile, formatProposalVersionForRfq } from '../../utils/proposalRfqVersionLink';
import { listProposalVersions } from '../../utils/proposalVersioning';
import {
  approvedCoverageForStructures,
  approvedQuoteCoverageForDeal,
} from '../../utils/rfqApprovedQuoteCoverage';
import {
  RFQ_RELEASE_PURPOSE_HINTS,
  RFQ_RELEASE_PURPOSE_LABELS,
  seedStagingStructureIds,
} from '../../utils/rfqReleasePurpose';
import '../pm/ReleaseToClientPortalModal.css';
import './ReleaseToRfqStagingModal.css';

const DISPLAY_LABEL_MAX = 60;
const EMPTY_PROPOSAL_STRUCTURES: ReadonlyArray<{ id: string; name: string }> = [];

export interface ReleaseToRfqStagingModalProps {
  dealId: string;
  timberProjectId: string;
  timberProjectName?: string;
  dealTitle?: string;
  streamKind: RfqStagingReleasableStreamKind;
  streamTitle: string;
  /** Structures the track is tagged to — seeds the supplier-facing name ("Sales Barn timber list"). */
  structureNames?: string[];
  /** Structure ids matching `structureNames`; used to detect an existing approved quote. */
  structureIds?: string[];
  /** Proposal structures staff can assign when the track was tagged wrong. */
  proposalStructures?: ReadonlyArray<{ id: string; name: string }>;
  versions: ProjectFile[];
  initialVersionId?: string | null;
  onClose: () => void;
  onReleased?: (result: {
    releaseId: string;
    rfqProjectId: string;
    releasedUrl: string;
    name: string;
  }) => void;
}

const ReleaseToRfqStagingModal: React.FC<ReleaseToRfqStagingModalProps> = ({
  dealId,
  timberProjectId,
  timberProjectName,
  dealTitle,
  streamKind,
  streamTitle,
  structureNames,
  structureIds,
  proposalStructures,
  versions,
  initialVersionId,
  onClose,
  onReleased,
}) => {
  const { rfqProjects, rfqPackages, rfqSubmissions } = useCRM();
  const workType = streamKind === 'timber_list_csv' ? 'timber_rfq' : 'cnc_quote';
  const structures = proposalStructures ?? EMPTY_PROPOSAL_STRUCTURES;
  const canEditStructures = structures.length > 0;
  const [selectedStructureIds, setSelectedStructureIds] = useState<string[]>(() =>
    seedStagingStructureIds(structures, structureIds, structureNames),
  );
  const structuresTouched = useRef(false);
  const labelTouched = useRef(false);

  useEffect(() => {
    if (structuresTouched.current) return;
    setSelectedStructureIds(seedStagingStructureIds(structures, structureIds, structureNames));
  }, [structures, structureIds, structureNames]);

  const selectedStructures = useMemo(() => {
    const byId = new Map(structures.map((structure) => [structure.id, structure]));
    return selectedStructureIds
      .map((id) => byId.get(id))
      .filter((structure): structure is { id: string; name: string } => !!structure);
  }, [structures, selectedStructureIds]);

  // Has a quote for this work type already been approved on the structures this
  // file covers? Then the default is a reference release (cut from, not requoted).
  const approvedCoverage = useMemo(
    () =>
      approvedCoverageForStructures(
        approvedQuoteCoverageForDeal({ dealId, workType, rfqProjects, rfqPackages, rfqSubmissions }),
        canEditStructures ? selectedStructures.map((structure) => structure.id) : structureIds,
        canEditStructures ? selectedStructures.map((structure) => structure.name) : structureNames,
      ),
    [
      dealId,
      workType,
      rfqProjects,
      rfqPackages,
      rfqSubmissions,
      canEditStructures,
      selectedStructures,
      structureIds,
      structureNames,
    ],
  );
  const offerReference = approvedCoverage !== 'none';
  const [releasePurpose, setReleasePurpose] = useState<RfqReleasePurpose>(() =>
    approvedCoverage === 'all' ? 'reference' : 'quote',
  );
  const coverageSeen = useRef<string | null>(null);
  useEffect(() => {
    if (coverageSeen.current === null) {
      coverageSeen.current = approvedCoverage;
      return;
    }
    if (coverageSeen.current === approvedCoverage) return;
    coverageSeen.current = approvedCoverage;
    setReleasePurpose(approvedCoverage === 'all' ? 'reference' : 'quote');
  }, [approvedCoverage]);
  const scopeNames = (structureNames ?? []).map((n) => (n || '').trim()).filter(Boolean);

  const sortedVersions = useMemo(
    () => [...versions].sort(compareVersionsNewestFirst),
    [versions],
  );
  const newestVersionId = sortedVersions[0]?.id || '';

  const [selectedFileId, setSelectedFileId] = useState<string>(() => {
    if (initialVersionId) return initialVersionId;
    return sortedVersions[0]?.id || '';
  });
  const [displayLabel, setDisplayLabel] = useState(
    () => defaultRfqStagingDisplayLabel(streamKind, structureNames) || streamTitle,
  );

  useEffect(() => {
    if (labelTouched.current || !canEditStructures) return;
    const names = selectedStructures.map((structure) => structure.name);
    setDisplayLabel(defaultRfqStagingDisplayLabel(streamKind, names) || streamTitle);
  }, [canEditStructures, selectedStructures, streamKind, streamTitle]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proposalVersions, setProposalVersions] = useState<Awaited<ReturnType<typeof listProposalVersions>>>([]);
  const [proposalVersionsLoading, setProposalVersionsLoading] = useState(false);
  const [linkedProposalVersionId, setLinkedProposalVersionId] = useState('');
  const [appliedProposalFileId, setAppliedProposalFileId] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const selectedFile = useMemo(
    () => sortedVersions.find((v) => v.id === selectedFileId) || null,
    [sortedVersions, selectedFileId],
  );

  useEffect(() => {
    if (streamKind !== 'timber_list_csv' || !dealId) return;
    let cancelled = false;
    setProposalVersionsLoading(true);
    void (async () => {
      try {
        const [versions, builderSnap] = await Promise.all([
          listProposalVersions(dealId, 25),
          getDoc(doc(db, 'proposalBuilders', dealId)),
        ]);
        if (cancelled) return;
        setProposalVersions(versions);
        const applied = builderSnap.exists()
          ? ((builderSnap.data() as { timberListAppliedToProposalFileId?: string | null })
              .timberListAppliedToProposalFileId ?? null)
          : null;
        setAppliedProposalFileId(applied);
      } catch {
        if (!cancelled) setProposalVersions([]);
      } finally {
        if (!cancelled) setProposalVersionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId, streamKind]);

  useEffect(() => {
    if (streamKind !== 'timber_list_csv' || !selectedFile?.id) return;
    const inferred = inferProposalLinkFromAppliedFile(
      appliedProposalFileId,
      selectedFile.id,
      proposalVersions,
    );
    setLinkedProposalVersionId(inferred?.id || '');
  }, [streamKind, selectedFile?.id, appliedProposalFileId, proposalVersions]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const workTypeLabel = streamKind === 'timber_list_csv' ? 'Timber RFQ' : 'CNC quote';
  const approvedQuoteNoun = streamKind === 'timber_list_csv' ? 'A timber quote' : 'A CNC quote';
  const canSubmit = !!selectedFile?.id && !submitting;

  const handleRelease = async () => {
    if (!selectedFile?.id) {
      setError('Pick a version to stage.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await releaseProjectFileToRfqStaging({
        dealId,
        timberProjectId,
        timberProjectName,
        dealTitle,
        projectFileId: selectedFile.id,
        displayLabel: displayLabel.trim() || streamTitle,
        linkedProposalVersionId: linkedProposalVersionId.trim() || undefined,
        releasePurpose: offerReference ? releasePurpose : 'quote',
        overrideStructures: canEditStructures,
        structureIds: selectedStructures.map((structure) => structure.id),
        structureNames: selectedStructures.map((structure) => structure.name),
      });
      onReleased?.(result);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not stage this release. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="pm-release-modal-overlay"
      role="presentation"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="pm-release-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-release-supplier-modal-title"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pm-release-modal-header">
          <span className="pm-release-modal-icon" aria-hidden>
            <Send size={18} />
          </span>
          <div>
            <h3 id="pm-release-supplier-modal-title">Release to suppliers</h3>
            <p className="pm-release-modal-subtitle">
              {streamTitle || workTypeLabel}
            </p>
          </div>
          <button
            type="button"
            className="pm-release-modal-close"
            onClick={() => {
              if (!submitting) onClose();
            }}
            aria-label="Close"
            disabled={submitting}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="pm-release-modal-body">
          <p className="pm-release-modal-hint">
            Stages this version for the {workTypeLabel.toLowerCase()} work order in Supplier Portal. A work
            order is created automatically if one does not exist yet — staff add suppliers and publish from
            there.
          </p>

          <div className="pm-release-modal-preview rfq-stage-scope">
            <span className="pm-release-modal-preview-label">Covers</span>
            {canEditStructures ? (
              <>
                <div className="rfq-stage-structure-list" role="group" aria-label="Structures this release covers">
                  <label className="rfq-stage-structure-option">
                    <input
                      type="checkbox"
                      checked={selectedStructureIds.length === 0}
                      disabled={submitting}
                      onChange={() => {
                        structuresTouched.current = true;
                        setSelectedStructureIds([]);
                      }}
                    />
                    <span>All structures</span>
                  </label>
                  {structures.map((structure) => {
                    const checked = selectedStructureIds.includes(structure.id);
                    return (
                      <label key={structure.id} className="rfq-stage-structure-option">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={submitting}
                          onChange={() => {
                            structuresTouched.current = true;
                            setSelectedStructureIds((prev) =>
                              prev.includes(structure.id)
                                ? prev.filter((id) => id !== structure.id)
                                : [...prev, structure.id],
                            );
                          }}
                        />
                        <span>{structure.name}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="rfq-stage-scope-hint">
                  Check the structures this file should cover. All structures covers the whole project.
                </p>
              </>
            ) : (
              <span className="rfq-stage-scope-chips">
                {scopeNames.length === 0 ? (
                  <span className="rfq-stage-scope-chip rfq-stage-scope-chip--all">All structures</span>
                ) : (
                  scopeNames.map((name) => (
                    <span key={name} className="rfq-stage-scope-chip">
                      {name}
                    </span>
                  ))
                )}
              </span>
            )}
          </div>

          {offerReference && (
            <div
              className="pm-release-modal-field rfq-stage-purpose"
              role="radiogroup"
              aria-labelledby="pm-release-purpose-heading"
            >
              <div id="pm-release-purpose-heading" className="pm-release-modal-section-label">
                Release type
              </div>
              <p className="pm-release-modal-hint-row rfq-stage-purpose-why">
                {approvedCoverage === 'all'
                  ? `${approvedQuoteNoun} is already approved for ${
                      scopeNames.length ? 'these structures' : 'this project'
                    }.`
                  : `${approvedQuoteNoun} is already approved for some of these structures.`}{' '}
                Choose whether suppliers should price this version or just build from it.
              </p>
              {(['reference', 'quote'] as RfqReleasePurpose[]).map((purpose) => {
                const isSel = releasePurpose === purpose;
                return (
                  <label
                    key={purpose}
                    className={`pm-release-version-row rfq-stage-purpose-row rfq-stage-purpose-row--${purpose} ${
                      isSel ? 'pm-release-version-row--active' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="pm-release-purpose"
                      value={purpose}
                      checked={isSel}
                      onChange={() => setReleasePurpose(purpose)}
                      disabled={submitting}
                    />
                    <span className="pm-release-version-main">
                      <span className="pm-release-version-title">
                        {RFQ_RELEASE_PURPOSE_LABELS[purpose]}
                        {purpose === 'reference' && (
                          <span className="rfq-stage-purpose-badge">Not requoted</span>
                        )}
                      </span>
                      <span className="pm-release-version-meta">{RFQ_RELEASE_PURPOSE_HINTS[purpose]}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <div
            className="pm-release-version-list"
            role="group"
            aria-labelledby="pm-release-staging-version-heading"
          >
            <div id="pm-release-staging-version-heading" className="pm-release-modal-section-label">
              Version
            </div>
            {sortedVersions.length === 0 ? (
              <div className="pm-release-empty">No versions available in this history.</div>
            ) : (
              sortedVersions.map((v) => {
                const isSel = v.id === selectedFileId;
                return (
                  <label
                    key={v.id}
                    className={`pm-release-version-row ${isSel ? 'pm-release-version-row--active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="pm-release-supplier-version"
                      value={v.id || ''}
                      checked={isSel}
                      onChange={() => v.id && setSelectedFileId(v.id)}
                      disabled={submitting || !v.id}
                    />
                    <span className="pm-release-version-main">
                      <span className="pm-release-version-title">
                        {v.versionLabel || `Version ${v.versionIndex ?? ''}`.trim()}
                        {v.versionIndex != null ? (
                          <span className="pm-release-version-index">{`#${v.versionIndex}`}</span>
                        ) : null}
                        {v.id && v.id === newestVersionId ? (
                          <span className="pm-release-version-head">Latest</span>
                        ) : null}
                      </span>
                      <span className="pm-release-version-meta">
                        {v.name}
                        <span aria-hidden> · </span>
                        {v.uploadedByName || 'Unknown'}
                        <span aria-hidden> · </span>
                        {formatDistanceToNow(v.uploadedAt, { addSuffix: true })}
                      </span>
                    </span>
                  </label>
                );
              })
            )}
          </div>

          <div className="pm-release-modal-field">
            <label htmlFor="pm-release-supplier-display-label" className="pm-release-modal-section-label">
              Display label
            </label>
            <input
              id="pm-release-supplier-display-label"
              type="text"
              className="pm-release-modal-input"
              value={displayLabel}
              maxLength={DISPLAY_LABEL_MAX}
              onChange={(e) => {
                labelTouched.current = true;
                setDisplayLabel(e.target.value);
              }}
              placeholder={defaultRfqStagingDisplayLabel(streamKind, structureNames)}
              disabled={submitting}
            />
            <p className="pm-release-modal-hint-row">Shown in Supplier Portal when staff publish packages.</p>
          </div>

          {streamKind === 'timber_list_csv' && (
            <div className="pm-release-modal-field">
              <label htmlFor="pm-release-linked-proposal-version" className="pm-release-modal-section-label">
                Linked proposal version
              </label>
              {proposalVersionsLoading ? (
                <p className="pm-release-modal-hint-row">Loading proposal versions…</p>
              ) : proposalVersions.length === 0 ? (
                <p className="pm-release-modal-hint-row">
                  No saved proposal versions yet — staging will auto-link when possible after versions exist.
                </p>
              ) : (
                <>
                  <select
                    id="pm-release-linked-proposal-version"
                    className="pm-release-modal-input"
                    value={linkedProposalVersionId}
                    disabled={submitting}
                    onChange={(e) => setLinkedProposalVersionId(e.target.value)}
                  >
                    <option value="">Auto-detect from applied timber list</option>
                    {proposalVersions.map((v) => (
                      <option key={v.id} value={v.id}>
                        {formatProposalVersionForRfq(v)}
                      </option>
                    ))}
                  </select>
                  <p className="pm-release-modal-hint-row">
                    Links this RFQ release to the proposal snapshot suppliers and estimators see in version history.
                  </p>
                </>
              )}
            </div>
          )}

          {selectedFile && (
            <div className="pm-release-modal-preview">
              <span className="pm-release-modal-preview-label">Source file</span>
              <code className="pm-release-modal-preview-name" title={selectedFile.name}>
                {selectedFile.name}
              </code>
            </div>
          )}

          {error && (
            <div className="pm-release-modal-error" role="alert">
              <AlertCircle size={14} aria-hidden /> {error}
            </div>
          )}
        </div>

        <footer className="pm-release-modal-actions">
          <button
            type="button"
            className="pm-release-modal-btn pm-release-modal-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="pm-release-modal-btn pm-release-modal-btn--primary"
            onClick={() => void handleRelease()}
            disabled={!canSubmit}
            aria-label="Stage this version for suppliers"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="pm-release-modal-spin" aria-hidden /> Staging…
              </>
            ) : (
              <>
                <Send size={14} aria-hidden />{' '}
                {offerReference && releasePurpose === 'reference' ? 'Stage reference release' : 'Stage release'}
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ReleaseToRfqStagingModal;
